import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useUIConfig } from '@/features/auth/hooks/useUIConfig'
import { authKeys } from '@/features/auth/api/auth.keys'
import { Button } from '@/ui/button'
import { Input } from '@/ui/input'
import { Label } from '@/ui/label'
import { toast } from 'sonner'
import { useUploadLogo } from '@/features/appearence/hooks/useUploadLogo'
import { useUpdateBranding } from '@/features/appearence/hooks/useUpdateBranding'
import styles from './Branding.module.scss'
import { Separator } from '@/ui/separator'

export default function Branding() {
  const [platformName, setPlatformName] = useState('Dhira Platform')
  const [slogan, setSlogan] = useState('')
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const [uploadedLogoUrl, setUploadedLogoUrl] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const queryClient = useQueryClient()
  const { data: uiConfig, isLoading, error } = useUIConfig()

  const uploadMutation = useUploadLogo()
  const updateMutation = useUpdateBranding()

  useEffect(() => {
    if (uiConfig?.data) {
      setPlatformName(uiConfig.data.ui_config.platform_name || 'Dhira Platform')
      setSlogan(uiConfig.data.ui_config.platform_slogan || '')
      setLogoPreview(uiConfig.data.logo || null)
      setUploadedLogoUrl(null)
    }
  }, [uiConfig])

  const isUploading = uploadMutation.status === 'pending'
  const isSaving = updateMutation.status === 'pending'

  if (isLoading) {
    return <div className={styles.loadingMessage}>Loading branding configuration...</div>
  }

  if (error) {
    return <div className={styles.errorMessage}>Error loading branding configuration.</div>
  }

  const handleBrowse = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const localPreviewUrl = URL.createObjectURL(file)
    setLogoPreview(localPreviewUrl)
    setUploadedLogoUrl(null)

    uploadMutation.mutate(file, {
      onSuccess: (url) => {
        setUploadedLogoUrl(url)
        setLogoPreview(url)
        toast.success('Logo uploaded successfully.')
      },
      onError: (error) => {
        console.error('Logo upload failed', error)
        toast.error('Logo upload failed. Please try again.')
      },
    })
  }

  const submitConfig = async () => {
    const logoUrl = uploadedLogoUrl || logoPreview

    if (!logoUrl) {
      toast.error('Upload a logo first.')
      return
    }

    updateMutation.mutate(
      {
        logo: logoUrl,
        config_json: {
          platform_name: platformName,
          platform_slogan: slogan,
        },
        modified_by: 'tenant_admin',
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(authKeys.uiConfig() as any)
          toast.success('Branding config saved successfully.')
        },
        onError: (error) => {
          console.error('Saving branding config failed', error)
          toast.error('Saving branding config failed.')
        },
      }
    )
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.sectionCard}>
        <div className={styles.header}>
          <h2 className={styles.title}>Platform Branding</h2>
          <p className={styles.subtitle}>Upload logo and configure platform name</p>
        </div>
        <Separator />
        <div className={styles.form}>
          {/* Platform Logo */}
          <div className={styles.field}>
            <Label className={styles.label}>Platform Logo</Label>
            <div className={styles.logoRow}>
              <div className={styles.logoBox}>
                {logoPreview
                  ? <img src={logoPreview} alt="Logo" className={styles.logoPreview} />
                  : <span className={styles.logoPlaceholder}>Upload Logo</span>
                }
              </div>
              <Button onClick={handleBrowse}>Browse files</Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className={styles.hiddenInput}
                onChange={handleFileChange}
              />
            </div>
          </div>

          {/* Platform Name */}
          <div className={styles.field}>
            <Label className={styles.label}>Platform Name</Label>
            <Input
              value={platformName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPlatformName(e.target.value)}
              placeholder="Platform Name"
              className={styles.inpt}
            />
          </div>

          {/* Slogan */}
          <div className={styles.field}>
            <Label className={styles.label}>Slogan</Label>
            <Input
              value={slogan}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSlogan(e.target.value)}
              placeholder="Lorem Ipsum"
              className={styles.inpt}
            />
          </div>

          {/* ✅ Action — Cancel added before Save */}
          <div className={styles.action}>
            <Button
              variant="outline"
              className={styles.cnloutlineCustom}
              onClick={() => {}}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={submitConfig}
              disabled={isSaving || !logoPreview}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
            {isUploading && <span className={styles.statusText}>Uploading file...</span>}
            {uploadedLogoUrl && !isUploading && <span className={styles.statusText}>Uploaded ✓</span>}
          </div>
        </div>
      </div>
    </div>
  )
}